import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import NeilMethodSection from "@/components/NeilMethodSection";
import SEO from "@/components/SEO";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Check } from "lucide-react";

const BOOKING_URL = "https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics";

const STAGES = [
  { name: "Discovery", body: "Stakeholder interviews across operations, facilities, finance, and IT to define the problem in your language." },
  { name: "Assessment", body: "On-site walkthrough, task timing, floorplan review, and a written automation opportunity map." },
  { name: "Pilot", body: "A contained deployment on one floor, one shift, or one property with agreed success measures." },
  { name: "Validation", body: "Measured results reviewed against the pilot criteria before any expansion is approved." },
  { name: "Multi-Site Rollout", body: "The validated playbook replicated site by site with local training and change management." },
  { name: "Fleet Optimization", body: "Route tuning, utilization review, preventive maintenance, and consolidated reporting." },
  { name: "Expansion", body: "New categories layered in — cleaning, delivery, outdoor, and digital AI workforce." },
];

const PROCUREMENT = [
  "Written scope, deliverables, and responsibilities before deployment",
  "Named deployment lead and escalation path",
  "Operator, supervisor, and administrator training",
  "Documented safety review and site conditions checklist",
  "Insurance, W-9, and vendor onboarding documentation on request",
  "Manufacturer identification preserved in technical, warranty, and safety documentation",
  "Consolidated invoicing across sites",
  "Quarterly business reviews with usage reporting",
];

const CAPABILITIES = [
  { h: "Facility-Wide Automation", p: "Cleaning, delivery, material transport, outdoor grounds, and digital AI workforce planned as one system rather than isolated purchases." },
  { h: "Multi-Site Standardization", p: "One deployment playbook, one training standard, and one support process across every property in the portfolio." },
  { h: "Change Management", p: "Automation lands on people. We train supervisors, brief teams, and position robots as capacity, not replacement." },
  { h: "Integration Support", p: "Network, elevator, access control, and CRM/workflow integration scoped during assessment, not discovered after delivery." },
  { h: "Fleet Reporting", p: "Coverage, utilization, and service history reporting your operations and finance teams can both use." },
  { h: "Single Accountability", p: "Physical robotics, outdoor automation, and digital AI under one partner and one contact." },
];

const Enterprise = () => (
  <>
    <SEO
      title="Enterprise Automation Programs | Neil AI Global Solutions"
      description="Enterprise robotics and AI automation built to scale: discovery, assessment, pilot, validation, multi-site rollout, fleet optimization, and expansion."
      keywords="enterprise robotics, multi-site robot deployment, enterprise automation program, fleet optimization, robotics pilot program"
    />
    <Navigation />

    <main className="min-h-screen bg-background pt-32 md:pt-40 pb-20">
      <div className="max-w-7xl mx-auto px-4 space-y-16">
        <section className="rounded-3xl border border-[hsl(var(--line))] glass-effect p-8 md:p-14">
          <Badge className="mb-4 bg-primary/15 text-primary border border-primary/30">
            Enterprise
          </Badge>
          <h1 className="font-orbitron text-3xl md:text-5xl font-bold leading-tight max-w-4xl">
            Automation Built to Scale.
          </h1>
          <p className="mt-5 max-w-3xl text-muted-foreground md:text-lg leading-relaxed">
            Enterprise automation fails when it starts with hardware. Neil AI starts with the
            workflow, proves it in a pilot, and only then replicates it across the portfolio —
            physical robotics and digital AI under one strategy.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild size="lg" className="rounded-xl font-bold">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                Get an Automation Assessment <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-xl font-bold border-primary/40">
              <Link to="/raas">Explore RaaS</Link>
            </Button>
          </div>
        </section>

        <section>
          <h2 className="font-orbitron text-2xl md:text-3xl font-bold mb-8">The Enterprise Path</h2>
          <div className="space-y-3">
            {STAGES.map((s, i) => (
              <Card
                key={s.name}
                className="glass-effect border-[hsl(var(--line))] p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-3 md:gap-6"
              >
                <div className="flex items-center gap-4 md:w-72 shrink-0">
                  <span className="font-mono text-sm text-primary">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="font-orbitron font-bold text-lg">{s.name}</h3>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.body}</p>
              </Card>
            ))}
          </div>
        </section>

        <section>
          <h2 className="font-orbitron text-2xl md:text-3xl font-bold mb-8">
            What Enterprise Clients Get
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {CAPABILITIES.map((c) => (
              <Card key={c.h} className="glass-effect border-[hsl(var(--line))] p-6">
                <h3 className="font-orbitron font-bold text-base mb-2">{c.h}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{c.p}</p>
              </Card>
            ))}
          </div>
        </section>

        <section className="rounded-3xl border border-[hsl(var(--line))] glass-effect p-8 md:p-12">
          <h2 className="font-orbitron text-2xl md:text-3xl font-bold mb-3">
            Built for Procurement
          </h2>
          <p className="text-muted-foreground max-w-3xl mb-8">
            Documentation, accountability, and process your procurement and legal teams can actually
            approve.
          </p>
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
            {PROCUREMENT.map((p) => (
              <div key={p} className="flex items-start gap-3">
                <Check className="h-4 w-4 text-primary mt-1 shrink-0" />
                <span className="text-sm text-muted-foreground">{p}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="rounded-3xl border border-primary/30 bg-primary/[0.05] p-8 md:p-14 text-center">
          <h2 className="font-orbitron text-2xl md:text-4xl font-bold mb-4">
            One partner. One automation strategy.
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto mb-7">
            Robotics, outdoor automation, and AI workforce systems planned together — assessed,
            piloted, and scaled with the same team from day one.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Button asChild size="lg" className="rounded-xl font-bold">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                Get an Automation Assessment
              </a>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-xl font-bold border-primary/40">
              <Link to="/ai-workforce">See the AI Workforce</Link>
            </Button>
          </div>
        </section>
      </div>

      <div className="max-w-7xl mx-auto px-4 mt-16">
        <div className="px-4"><NeilMethodSection compact contextLabel="STRUCTURED DISCOVERY BEFORE ENTERPRISE DEPLOYMENT" contextCopy="Enterprise deployments start with documented discovery. The N.E.I.L.™ Method gives procurement, facilities, and finance teams a defensible record of need, environment, inputs, and baseline economics before capital is committed." /></div>
        <Footer />
      </div>
    </main>
  </>
);

export default Enterprise;
